/**
 * ConversationWrapper component
 * Fetches user profile and subscription status before rendering ConversationUI
 * Handles routing based on subscription status
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../config/supabase';
import ConversationUI from './ConversationUI.js';
import LoadingScreen from './common/LoadingScreen';

const ConversationWrapper = () => {
  const { session, profile, subscriptionStatus, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [loadingTimeout, setLoadingTimeout] = useState(false);

  // Add timeout to prevent infinite loading
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (authLoading || !profile) {
        console.log('ConversationWrapper: Loading timeout reached, showing error');
        setLoadingTimeout(true);
      }
    }, 10000); // 10 second timeout

    return () => clearTimeout(timeoutId);
  }, [authLoading, profile]);

  useEffect(() => {
    const handleRouting = () => {
      const urlParams = new URLSearchParams(window.location.search);
      const subscriptionSuccess = urlParams.get('subscription') === 'success';
      
      console.log('ConversationWrapper: Handling routing', {
        authLoading,
        hasSession: !!session?.user,
        hasProfile: !!profile,
        subscriptionStatus,
        subscriptionSuccess,
        quizCompleted: profile?.quiz_completed,
        profileType: typeof profile?.quiz_completed,
        profileKeys: profile ? Object.keys(profile) : null
      });

      // Wait for auth to finish loading completely
      if (authLoading) {
        console.log('ConversationWrapper: Still loading auth, waiting...');
        return;
      }

      if (!session?.user) {
        console.log('ConversationWrapper: No session, redirecting to login');
        navigate('/login');
        return;
      }

      // If coming from successful subscription, be more permissive
      if (subscriptionSuccess) {
        console.log('ConversationWrapper: Subscription success detected, being more permissive');
        if (profile) {
          console.log('ConversationWrapper: Profile exists, allowing access for subscription success');
          setError(null);
          return;
        }
        // Give more time for profile to load after subscription success
        if (!profile) {
          console.log('ConversationWrapper: Profile still loading after subscription success, waiting...');
          return;
        }
      }

      // Give useAuth more time to load profile data after session is established
      // This prevents race condition with LoginForm's fresh database query
      if (!profile && session?.user) {
        console.log('ConversationWrapper: Session exists but profile still loading, waiting...');
        return;
      }

      if (!profile) {
        console.log('ConversationWrapper: No profile found, redirecting to quiz');
        navigate('/quiz');
        return;
      }

      // Check if quiz is completed - be more defensive about data types
      const quizCompleted = profile.quiz_completed === true || profile.quiz_completed === 'true';
      if (!quizCompleted) {
        console.log('ConversationWrapper: Quiz not completed (value:', profile.quiz_completed, ', type:', typeof profile.quiz_completed, '), redirecting to quiz');
        navigate('/quiz');
        return;
      }

      // Check subscription status - only active subscriptions allowed
      const isSubscriptionActive = subscriptionStatus === 'active';

      if (!isSubscriptionActive) {
        console.log('ConversationWrapper: Subscription not active:', subscriptionStatus);
        const message = subscriptionStatus === 'expired'
          ? 'Your subscription has expired. Please renew to continue.'
          : 'Please complete your subscription to access the platform.';
        
        navigate('/subscribe', { state: { message } });
        return;
      }

      console.log('ConversationWrapper: All checks passed, loading conversation');
      setError(null);
    };

    // Add a longer delay to ensure auth state is fully settled and profile is loaded
    const timeoutId = setTimeout(handleRouting, 500);
    
    return () => clearTimeout(timeoutId);
  }, [session, profile, subscriptionStatus, authLoading, navigate]);

  if (authLoading) {
    return <LoadingScreen message="Loading your therapy session..." />;
  }

  if (error) {
    return (
      <div style={{ 
        display: 'flex', 
        flexDirection: 'column', 
        alignItems: 'center', 
        justifyContent: 'center', 
        minHeight: '100vh',
        padding: '16px',
        textAlign: 'center',
        maxWidth: '100%'
      }}>
        <h3 style={{ 
          fontSize: 'clamp(1.25rem, 5vw, 1.5rem)',
          margin: '0 0 16px 0',
          color: '#1a1a1a'
        }}>
          Unable to Load Session
        </h3>
        <p style={{
          fontSize: 'clamp(0.875rem, 4vw, 1rem)',
          margin: '0 0 20px 0',
          color: '#666',
          maxWidth: '400px',
          lineHeight: '1.5'
        }}>
          {error}
        </p>
        <button 
          onClick={() => window.location.reload()}
          style={{
            padding: '12px 24px',
            backgroundColor: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            cursor: 'pointer',
            fontSize: '1rem',
            fontWeight: '500',
            minHeight: '44px',
            minWidth: '120px'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  if (!profile) {
    return <LoadingScreen message="Initializing..." />;
  }

  return <ConversationUI userProfile={profile} />;
};

export default ConversationWrapper;
